import {
  Button as AriaButton,
  Tooltip,
  TooltipTrigger,
  type ButtonProps as AriaButtonProps,
} from 'react-aria-components';

import { useAriaKeyShortcuts } from '../../shortcuts.ts';
import { Kbd } from '../Kbd/Kbd.tsx';
import styles from './Button.module.css';

export type ButtonVariant = 'primary' | 'secondary' | 'quiet' | 'destructive';

export interface ButtonProps extends Omit<
  AriaButtonProps,
  'children' | 'className' | 'style' | 'isPending'
> {
  /** Plain text saying what happens: "Take payment", not "Submit". No arrows or emoji. */
  children: string;
  /**
   * `primary`: one per view. `destructive`: only inside confirmation dialogs.
   * Defaults to `secondary` so a primary is always a deliberate choice.
   */
  variant?: ButtonVariant;
  /** 36px tall, for dense tables. */
  dense?: boolean;
  /** While true, shows `pendingLabel` ("Sending…") and ignores presses, but keeps focus. */
  isPending?: boolean;
  pendingLabel?: string;
  /**
   * The keyboard shortcut for this action, as a `useShortcut` combo ("mod+enter", "n").
   * Shown in a tooltip on hover and focus and exposed as `aria-keyshortcuts`. The screen
   * still registers the shortcut itself with `useShortcut`; this only makes it visible.
   */
  shortcut?: string;
}

const TOOLTIP_DELAY_MS = 600;

export function Button({
  children,
  variant = 'secondary',
  dense = false,
  isPending = false,
  pendingLabel,
  onPress,
  shortcut,
  ...rest
}: ButtonProps) {
  const shortcutRef = useAriaKeyShortcuts(shortcut);
  // Pending is handled here rather than with React Aria's isPending, whose live
  // announcement leaves an unlabelled role="img" node behind (an axe failure). The
  // visible label already says what is happening; the button stays focusable.
  const button = (
    <AriaButton
      {...rest}
      {...(onPress === undefined || isPending ? {} : { onPress })}
      {...(isPending ? { 'aria-disabled': true, type: 'button' as const } : {})}
      {...(shortcut === undefined ? {} : { ref: shortcutRef })}
      className={styles.button}
      data-variant={variant}
      data-dense={dense || undefined}
      data-pending={isPending || undefined}
    >
      {isPending && pendingLabel ? pendingLabel : children}
    </AriaButton>
  );
  if (shortcut === undefined) return button;
  return (
    <TooltipTrigger delay={TOOLTIP_DELAY_MS}>
      {button}
      <Tooltip className={styles.tooltip} offset={6}>
        <span>{children}</span>
        <Kbd combo={shortcut} />
      </Tooltip>
    </TooltipTrigger>
  );
}

/**
 * Props that give a link (a router `Link` or `<a>`) the look of a button, for actions
 * that go to another screen and sit in a row with real buttons. Hover and focus come from
 * `:hover` and `:focus-visible` in Button.module.css, since links have no React Aria states.
 */
export function buttonLinkProps({
  variant = 'secondary',
  dense = false,
}: { variant?: Exclude<ButtonVariant, 'destructive'>; dense?: boolean } = {}) {
  return {
    className: styles.button,
    'data-variant': variant,
    ...(dense ? { 'data-dense': true } : {}),
  };
}
